// Conversion Modal - Convert Novel to Screenplay/TV Script
import React, { useState, useEffect } from 'react';
import { api } from '../utils/api';
import { Button } from './ui/button';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from './ui/card';
import { Alert, AlertDescription } from './ui/alert';
import { Film, Tv, Loader2, Download, Check, X } from 'lucide-react';
import { ConversionPaymentModal } from './ConversionPaymentModal';

export function ConversionModal({ documentId, documentTitle, onClose }) {
  const [conversions, setConversions] = useState([]);
  const [selectedType, setSelectedType] = useState(null);
  const [converting, setConverting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [jobId, setJobId] = useState(null);
  const [progress, setProgress] = useState(0);
  const [progressMessage, setProgressMessage] = useState('');
  const [showPaymentModal, setShowPaymentModal] = useState(false);

  useEffect(() => {
    loadPricing();
  }, []);

  useEffect(() => {
    let pollInterval;
    
    if (jobId && converting) {
      // Poll for job status every 2 seconds
      pollInterval = setInterval(async () => {
        try {
          const status = await api.request(`/conversions/status/${jobId}`);
          
          setProgress(status.progress || 0);
          setProgressMessage(status.progress_message || '');
          
          if (status.status === 'completed') {
            setConverting(false);
            setResult({
              success: true,
              message: status.progress_message,
              new_document_id: status.new_document_id,
              estimated_pages: status.estimated_pages,
              word_count: status.word_count
            });
            clearInterval(pollInterval);
          } else if (status.status === 'failed') {
            setConverting(false);
            setError(status.error || 'Conversion failed');
            clearInterval(pollInterval);
          }
        } catch (err) {
          console.error('Failed to check status:', err);
        }
      }, 2000);
    }
    
    return () => {
      if (pollInterval) clearInterval(pollInterval);
    };
  }, [jobId, converting]);

  const loadPricing = async () => {
    try {
      const data = await api.request('/conversions/pricing/list');
      setConversions(data.conversions);
    } catch (err) {
      console.error('Failed to load pricing:', err);
    }
  };

  const handleConvert = async () => {
    if (!selectedType) return;
    
    // Show payment modal first
    setShowPaymentModal(true);
  };
  
  const handlePaymentComplete = async () => {
    // After payment is verified, start the actual conversion
    setConverting(true);
    setError('');
    setResult(null);
    setProgress(0);
    setProgressMessage('Starting conversion...');

    try {
      const response = await api.request('/conversions/convert', {
        method: 'POST',
        body: JSON.stringify({
          document_id: documentId,
          conversion_type: selectedType.type
        })
      });

      if (response.job_id) {
        setJobId(response.job_id);
        setProgressMessage(response.message || 'Conversion started...');
      } else {
        setError('Invalid response from server');
        setConverting(false);
      }
    } catch (err) {
      setError(err.message || 'Conversion failed');
      setConverting(false);
    }
  };

  const handleDownload = () => {
    if (!result) return;

    const blob = new Blob([result.output], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${documentTitle}_${result.conversion_type}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  if (result && result.success) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[200]">
        <Card className="max-w-2xl w-full max-h-[90vh] overflow-auto">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Check className="h-6 w-6 text-green-600" />
                  Conversion Complete!
                </CardTitle>
                <CardDescription className="mt-2">
                  Your {selectedType.name.toLowerCase()} is ready
                </CardDescription>
              </div>
              <Button onClick={onClose} variant="ghost" size="sm">
                <X className="h-4 w-4" />
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <Alert className="bg-green-50 border-green-200">
              <AlertDescription>
                <div className="space-y-2">
                  <p className="font-semibold text-green-900">✅ {result.message}</p>
                  <div className="text-sm text-green-800">
                    <p>📄 Estimated Pages: {result.estimated_pages || 'N/A'}</p>
                    <p>📝 Word Count: {result.word_count?.toLocaleString() || 'N/A'}</p>
                  </div>
                </div>
              </AlertDescription>
            </Alert>
            
            <div className="flex gap-2">
              <Button 
                onClick={() => window.location.href = `/editor/${result.new_document_id}`} 
                className="flex-1"
              >
                Open Screenplay
              </Button>
              <Button onClick={onClose} variant="outline">
                Close
              </Button>
            </div>

            <Alert>
              <AlertDescription>
                💡 <strong>Tip:</strong> Your screenplay has been saved to your documents. You can export it as PDF or edit it in the editor.
              </AlertDescription>
            </Alert>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[200]">
      <Card className="max-w-4xl w-full max-h-[90vh] overflow-auto">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-2xl">🎬 Convert to Screenplay/TV Script</CardTitle>
              <CardDescription className="mt-2">
                Transform "{documentTitle}" into Hollywood-ready format
              </CardDescription>
            </div>
            <Button onClick={onClose} variant="ghost" size="sm">
              <X className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {error && (
            <Alert variant="destructive" className="mb-4">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <div className="grid md:grid-cols-2 gap-4 mb-6">
            {conversions.filter(c => !c.coming_soon).map((conversion) => (
              <Card 
                key={conversion.type}
                className={`cursor-pointer transition-all ${
                  selectedType?.type === conversion.type 
                    ? 'border-purple-600 border-2 bg-purple-50' 
                    : 'hover:border-purple-300'
                } ${conversion.coming_soon ? 'opacity-50' : ''}`}
                onClick={() => !conversion.coming_soon && setSelectedType(conversion)}
              >
                <CardHeader>
                  <CardTitle className="flex items-center justify-between text-lg">
                    <span className="flex items-center gap-2">
                      {conversion.type === 'screenplay' ? <Film className="h-5 w-5" /> : <Tv className="h-5 w-5" />}
                      {conversion.name}
                    </span>
                    <span className="text-2xl font-bold text-purple-600">
                      ${conversion.price}
                    </span>
                  </CardTitle>
                  <CardDescription>{conversion.description}</CardDescription>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-1 text-sm text-gray-600">
                    {conversion.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start gap-2">
                        <Check className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                  {conversion.coming_soon && (
                    <div className="mt-3 px-3 py-1 bg-yellow-100 text-yellow-800 text-xs rounded-full inline-block">
                      Coming Soon
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>

          {selectedType && (
            <Alert className="mb-4 bg-blue-50 border-blue-200">
              <AlertDescription>
                <strong>Selected:</strong> {selectedType.name} for ${selectedType.price}
                <br />
                <span className="text-sm text-blue-700 mt-1 block">
                  🎬 Processing takes 2-5 minutes. Your script will use industry-standard formatting.
                </span>
              </AlertDescription>
            </Alert>
          )}

          {converting && (
            <Alert className="mb-4 bg-blue-50 border-blue-200">
              <AlertDescription>
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <Loader2 className="animate-spin h-4 w-4 text-blue-600" />
                    <span className="font-semibold text-blue-900">Converting...</span>
                  </div>
                  <div className="w-full bg-blue-200 rounded-full h-2">
                    <div 
                      className="bg-blue-600 h-2 rounded-full transition-all duration-500"
                      style={{ width: `${progress}%` }}
                    />
                  </div>
                  <p className="text-sm text-blue-700">{progressMessage}</p>
                  <p className="text-xs text-blue-600">
                    This may take 2-5 minutes. Please don't close this window.
                  </p>
                </div>
              </AlertDescription>
            </Alert>
          )}

          <div className="flex gap-2">
            <Button 
              onClick={handleConvert} 
              disabled={!selectedType || converting}
              className="flex-1"
            >
              {converting ? (
                <>
                  <Loader2 className="animate-spin h-4 w-4 mr-2" />
                  Converting... ({progress}%)
                </>
              ) : (
                <>
                  🎬 Convert Now
                </>
              )}
            </Button>
            <Button onClick={onClose} variant="outline" disabled={converting}>
              Cancel
            </Button>
          </div>

          <div className="mt-4 p-4 bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg border border-purple-200">
            <h4 className="font-semibold text-purple-900 mb-2">💎 Coming Soon:</h4>
            <ul className="text-sm text-purple-800 space-y-1">
              <li>• Limited Series (8 episodes) - $249</li>
              <li>• Full Netflix Season (12 episodes) - $399</li>
              <li>• Multi-Season Blueprint - $899</li>
            </ul>
            <p className="text-xs text-purple-700 mt-2">
              Phase 2 & 3 launching soon! Early access for Pro members.
            </p>
          </div>
        </CardContent>
      </Card>
      
      {/* Payment Modal */}
      {showPaymentModal && selectedType && (
        <ConversionPaymentModal
          conversionType={selectedType.type}
          documentTitle={documentTitle}
          onClose={() => setShowPaymentModal(false)}
          onPaymentComplete={handlePaymentComplete}
        />
      )}
    </div>
  );
}
